/**
 * `@martincastroalvarez/login/public` — the sign-in page's two requests: open a session, end one.
 *
 * ## Why a subpath of its own
 *
 * The package root is every console's client: axios, interceptors, a 401-to-login redirect, React
 * hooks. The sign-in page is the one surface that must have none of those — it is where the redirect
 * lands — and it is drawn in anubis with a boot budget a client library would spend on nothing. So
 * this entry is `fetch` and ra runtime only, and the page that bundles it stays as small as it was
 * when these calls lived in its own `src/useLogin.ts` and `src/session.ts` (until 2026-10-20).
 *
 * ## The page never writes the session cookies
 *
 * Since 2026-10-02 the token is an **HttpOnly** cookie that `POST /v1/session` sets with
 * `Set-Cookie`; script cannot read or clear it. Signing in is therefore a POST with
 * `credentials: 'include'` (the browser stores what it answers), and landing on the page ends any
 * session there with `DELETE /v1/session`, also with credentials.
 *
 * {@link endSession} is idempotent and {@link openSession} awaits it first: on an OAuth callback the
 * page mounts, starts the DELETE and the code exchange in the same tick, and a DELETE that landed
 * after the POST would sign the person straight back out.
 */
import { apiHost } from '@martincastroalvarez/runtime';

/** What `POST /v1/session` takes: a provider's code (+ redirect, + PKCE verifier) or an ID token. */
export interface SessionRequest {
  provider?: 'google' | 'linkedin' | 'facebook' | 'twitter';
  code?: string;
  redirect_uri?: string;
  /** X/Twitter only: the PKCE verifier this browser generated when it started the flow. */
  code_verifier?: string;
  /** A Google ID token (the button's credential). */
  token?: string;
}

/** Whose session it is and when it ends (unix seconds) — the token itself is the HttpOnly cookie. */
export interface OpenedSession {
  email: string;
  exp: number;
}

/**
 * The API refused the exchange. `body` is its raw answer — JSON `{ message }`, plain text, or the
 * HTML a 502 sends — for the page to word; the page knows which sentences are its own to translate.
 */
export class SessionRefusedError extends Error {
  readonly status: number;
  readonly body: string;

  constructor(status: number, body: string) {
    super(`The login API answered ${status}`);
    this.name = 'SessionRefusedError';
    this.status = status;
    this.body = body;
  }
}

/** `https://login.api.<apex>` — the tenant's login API, from ra runtime's apex. */
export function loginApiUrl(): string {
  return `https://${apiHost('login')}`;
}

let ending: Promise<void> | undefined;

/**
 * Sign the browser out (once per page load) and resolve when that has finished. Never rejects: a
 * sign-out that could not reach the API leaves an old session in place, which the next sign-in
 * replaces anyway — it must not block that sign-in.
 */
export function endSession(): Promise<void> {
  ending ??= fetch(`${loginApiUrl()}/v1/session`, {
    method: 'DELETE',
    credentials: 'include',
  }).then(
    () => undefined,
    () => undefined,
  );
  return ending;
}

/**
 * Exchange a provider's answer for a session: `POST /v1/session`, after any sign-out this page
 * started has finished. Throws {@link SessionRefusedError} on a refusal, and lets a network failure
 * (`TypeError: Failed to fetch`) through untouched — the page words the two differently.
 */
export async function openSession(request: SessionRequest): Promise<OpenedSession> {
  await endSession();
  const response = await fetch(`${loginApiUrl()}/v1/session`, {
    method: 'POST',
    // The answer SETS the session cookies; without credentials the browser drops them.
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    let body = '';
    try {
      body = await response.text();
    } catch {
      // No readable body (a 502 without CORS): the status alone is the answer.
    }
    throw new SessionRefusedError(response.status, body);
  }
  return (await response.json()) as OpenedSession;
}
